<template>
    <div class="layout-container-wrapper" :cid="cId" v-if="config && layoutTemplateLoaded" :data-test-id="dataTestId" :style="layoutBodyStyles" ref="masonryContainer">
        <settings-cog v-if="editorEnabled" :component-id="cId" :editor-enabled="editorEnabled" :test-mode="testMode"></settings-cog>
        <button v-if="config?.enablePrinting" type="button" @click="openPrintView" class="global-print-button"><i class="fa-solid fa-print"></i>Print</button>
        <div v-if="uiTagData" class="ui-tags">Has Tag Data</div>
        <dynamic-template :template="layoutTemplate"
            :storeName="storeName"
            :layoutDatamap="layoutDatamap"
            :data="config"
            :mapped-prop-data="mappedPropData"
            :computed-props="computedPropData"
            :layout-template-loaded="layoutTemplateLoaded"
            :unique-store-id="dataTestId"
            :editorEnabled="editorEnabled"
            @update:childComponentRefs="setChildRefs"
        ></dynamic-template>
    </div>
</template>

<script>
import {
    AccordionItemDirective, AccordionItemsDirective
} from "@syncfusion/ej2-vue-navigations/src/accordion/items.directive";
import {
    SplitterComponent,
    PanesDirective,
    PaneDirective,
    PanelDirective,
    PanelsDirective
} from "@syncfusion/ej2-vue-layouts";
import { mapGetters } from "vuex";
import {
    h, defineAsyncComponent, ref
} from 'vue';
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { uStore } from "@common/userStore";
import { recordStore } from "@common/recordStore";
import { store } from "@common/store/store";
import { mapState } from "pinia";
import Masonry from 'masonry-layout';
import { useVueToPrint } from "vue-to-print";

const enableHeaderLogic = [
    {
        record: 'self',
        valueType: 'criteria',
        criteriaRecord: 'component',
        prop: 'display',
        criteria: 'this.controls.settings_enableHeader.value == true'
    }
];

const iconItems = [
    { text: 'e-double-left-arrow', value: 'e-double-left-arrow' },
    { text: 'e-double-right-arrow', value: 'e-double-right-arrow' },
    { text: 'e-back-arrow', value: 'e-back-arrow' },
    { text: 'e-left-arrow', value: 'e-left-arrow' },
    { text: 'e-right-arrow', value: 'e-right-arrow' },
    { text: 'e-left-chevron', value: 'e-left-chevron' },
    { text: 'e-right-chevron', value: 'e-right-chevron' },
    { text: 'e-menu-1', value: 'e-menu-1' },
    { text: 'e-menu-2', value: 'e-menu-2' },
    { text: 'e-search', value: 'e-search' }
];

const editorSettings = {
    templateSettingsPanel: {
        type: 'panel',
        label: 'Template Settings',
        id: 'templateSettingsPanel',
        fields: [
            'templateFile',
            'preventScrollbars',
            'resizePanelClasses',
        ]
    },
    positionsPanel: {
        type: 'panel',
        label: 'Positions',
        id: 'positionsPanel',
        fields: [
            'positions',
        ]
    },
    expandCollapseSettingsPanel: {
        type: 'panel',
        label: 'Expand/Collapse Settings',
        id: 'expandCollapseSettingsPanel',
        fields: [
            'expandCollapseButtons',
            'defaultCollapsedPanel',
            'removeHiddenComponents'
        ]
    },
    childComponentSettingsPanel: {
        type: 'panel',
        label: 'Child Component Settings',
        id: 'childComponentSettingsPanel',
        fields: [
            'childComponentStyles',
            'position1ComponentSettings',
            'position2ComponentSettings',
        ]
    },
    generalSettingsPanel: {
        type: 'panel',
        label: 'General Settings',
        id: 'generalSettingsPanel',
        fields: [
            'settings'
        ]
    },
    displayIfPanel: {
        type: 'panel',
        label: 'Display Logic Settings',
        id: 'displayIfPanel',
        fields: [
            'displayIf',
            'showIf'
        ]
    },
    datamapSettings: {
        type: 'panel',
        label: 'Datamap Settings',
        id: 'datamapSettings',
        fields: [
            'layoutDatamap',
            'layoutDataLogic',
            'storeProps',
            'recordTags'
        ]
    },
    editorDescription: { type: "INFO", text: "The layoutContainer component is used to position components on the page. It is also used to map data connections between components, and other layouts." },
    templateFile: {
        type: 'dropdown',
        id: 'templateFile',
        itemsSource: 'configStore.getTemplateList()',
        itemsSort: {
            sortBy: 'name'
        },
        fields: { text: 'name', value: 'templateFile' },
        label: 'Template File',
        required: true,
        helpText: 'The template determines the HTML structure that should be used for the layout.',
        default: ''
    },
    position1ComponentSettings: {
        type: 'object',
        label: 'Position 1 Child Component Settings',
        helpText: 'Settings for the children components.',
        id: 'position1ComponentSettings',
        default: {
            componentPadding: '10px'
        },
        properties: {
            componentPadding: {
                type: 'string',
                label: 'Component Padding',
                helpText: 'Padding around your child components.',
                default: '10px',
                id: 'componentPadding',
                message: 'eg: 10px or 10px 15px 10px 0'
            },
        }
    },
    position2ComponentSettings: {
        type: 'object',
        label: 'Position 2 Child Component Settings',
        helpText: 'Settings for the children components.',
        id: 'position2ComponentSettings',
        default: {
            componentPadding: '10px'
        },
        properties: {
            componentPadding: {
                type: 'string',
                label: 'Component Padding',
                helpText: 'Padding around your child components.',
                default: '10px',
                id: 'componentPadding',
                message: 'eg: 10px or 10px 15px 10px 0'
            }
        }
    },
    childComponentStyles: {
        type: 'object',
        label: 'Child Component Styles',
        helpText: 'Styles for the children components.',
        id: 'childComponentStyles',
        default: {
            componentPadding: '10px',
            componentBoxShadow: '2px 2px 6px rgba(0,0,0, 0.10)',
            componentBackgroundColor: '#ffffff',
            componentBorderRadius: '1'
        },
        properties: {
            componentPadding: {
                type: 'string',
                label: 'Inner Component Padding',
                helpText: 'Padding around your child components.',
                default: '10px',
                id: 'componentPadding',
                message: 'eg: 10px or 10px 15px 10px 0'
            },
            componentBoxShadow: {
                type: 'string',
                label: 'Box Shadow Settings',
                helpText: 'Display a box shadow for all child components.',
                default: '2px 2px 6px rgba(0,0,0, 0.10)',
                id: 'componentBoxShadow',
                message: 'eg: 2px 2px 6px rgba(0,0,0, 0.10)'
            },
            componentBackgroundColor: {
                id: 'componentBackgroundColor',
                type: 'color',
                label: 'Component Background Color',
                helpText: 'Set the background color of child components.',
                default: '#000000ff'
            },
            componentBorderRadius: {
                id: 'componentBorderRadius',
                type: 'dropdown',
                items: [
                    { text: '0 none', value: '1' },
                    { text: '3px', value: '2' },
                    { text: '5px', value: '3' },
                    { text: '10px', value: '4' },
                ],
                fields: {
                    text: 'text',
                    value: 'value'
                },
                label: 'Component Border Radius',
                helpText: 'Set the border radius of child components.',
                default: '1'
            }
        }
    },
    settings: {
        type: 'object',
        label: 'General Layout Settings',
        helpText: 'Settings such as header and footer settings, column widths, container width etc.',
        default: {
            enableHeader: false,
            header: '',
            enableFooter: false,
            showComponentBorders: false
        },
        properties: {
            enableHeader: {
                type: 'boolean',
                label: 'Enable Header',
                helpText: 'Enable the header position',
                default: true,
                id: 'enableHeader'
            },
            header: {
                type: 'text',
                label: 'Header Text',
                helpText: 'Display header text.',
                default: '',
                id: 'header',
                logic: enableHeaderLogic
            },
            headerBgColor: {
                id: 'headerBgColor',
                type: 'color',
                label: 'Header Background Color',
                helpText: 'The background color of your header.',
                default: '#ccccccff',
                logic: enableHeaderLogic
            },
            headerLogoImage: {
                type: 'upload',
                label: 'Header Logo Image',
                id: 'headerLogoImage',
                allowedExtensions: '.png, .jpg, .jpeg',
                showAsDefaultInput: true,
                multiple: false,
                psp: 'component',
                helpText: 'Upload an image to display in the header.'
            },
            headerLogoTitle: {
                type: 'string',
                label: 'Logo Alt Text',
                id: 'headerLogoTitle',
                default: 'Header Logo',
                helpText: 'Text that is displayed when you hover your mouse over the image.'
            },
            headerLogoAltText: {
                type: 'string',
                label: 'Logo Alt Text',
                id: 'headerLogoAltText',
                default: 'Header Logo',
                helpText: 'Text that is displayed when the image does not load.'
            },
            headerLogoHeight: {
                type: 'string',
                label: 'Logo Height',
                id: 'headerLogoHeight',
                default: '40px',
                helpText: 'Set the height of the logo.  Note: The width will be determined by the aspect ratio.',
                message: 'eg: 40px'
            },
            headerLogoPadding: {
                type: 'string',
                label: 'Logo Padding',
                id: 'headerLogoPadding',
                default: '0',
                helpText: 'Set the padding around the logo.',
                message: 'eg: 0 10px 10px 0'
            },
            headerLogoMargin: {
                type: 'string',
                label: 'Logo Margin',
                id: 'headerLogoMargin',
                default: '10px',
                helpText: 'Set the margin around the logo.',
                message: 'eg: 0, 10px 10px 0'
            },
            textColor: {
                id: 'textColor',
                type: 'color',
                label: 'Header Text Color',
                helpText: 'The color of the text in your header.',
                default: '#000000ff',
                logic: enableHeaderLogic
            },
            enableFooter: {
                type: 'boolean',
                label: 'Enable Footer',
                helpText: 'Enable the footer position',
                default: true,
                id: 'enableFooter'
            },
            columnRatio: {
                id: 'columnRatio',
                type: 'dropdown',
                label: 'Column Size Ratio',
                helpText: 'Ratio of size for each column in the layout body',
                default: '1:1',
                items: [
                    { text: '1:1', value: '1:1', templateFile: 'staticTemplates/2_column.js' },
                    { text: '1:2', value: '1:2', templateFile: 'staticTemplates/2_column.js' },
                    { text: '1:3', value: '1:3', templateFile: 'staticTemplates/2_column.js' },
                    { text: '2:1', value: '2:1', templateFile: 'staticTemplates/2_column.js' },
                    { text: '3:1', value: '3:1', templateFile: 'staticTemplates/2_column.js' },
                    { text: '1:1:1', value: '1:1:1', templateFile: 'staticTemplates/3_column.js' },
                    { text: '2:1:1', value: '2:1:1', templateFile: 'staticTemplates/3_column.js' },
                    { text: '1:2:1', value: '1:2:1', templateFile: 'staticTemplates/3_column.js' },
                    { text: '1:1:2', value: '1:1:2', templateFile: 'staticTemplates/3_column.js' },
                    { text: '1:1:1:1', value: '1:1:1:1', templateFile: 'staticTemplates/4_column.js' },
                ],
                fields: {
                    text: 'text',
                    value: 'value'
                },
                itemsFilter: 'this.valuesMap.templateFile === this.item.templateFile',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_column.js" || this.valuesMap.templateFile == "staticTemplates/3_column.js" || this.valuesMap.templateFile == "staticTemplates/4_column.js"'
                    },
                    {
                        record: 'component',
                        valueType: 'value',
                        value: '1:1:1',
                        criteriaRecord: 'component',
                        prop: 'valuesMap.settings.columnRatio',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/3_column.js" && (!this.valuesMap.settings?.columnRatio || this.valuesMap.settings.columnRatio.match(/:/g).length !== 2)'
                    },
                    {
                        record: 'component',
                        valueType: 'value',
                        value: '1:1',
                        criteriaRecord: 'component',
                        prop: 'valuesMap.settings.columnRatio',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_column.js" && (!this.valuesMap.settings?.columnRatio || this.valuesMap.settings.columnRatio.match(/:/g).length !== 1)'
                    },
                    {
                        record: 'component',
                        valueType: 'value',
                        value: '1:1:1:1',
                        criteriaRecord: 'component',
                        prop: 'valuesMap.settings.columnRatio',
                        criteria: 'this.valuesMap.templateFile === "staticTemplates/4_column.js"'
                    }
                ]
            },
            layoutWidthType: {
                id: 'layoutWidthType',
                type: 'dropdown',
                label: 'Layout Width Type',
                helpText: 'Layout width can be 100% of screen size (container-fluid), or centered with a max width.',
                items: [
                    'container',
                    'container-sm',
                    'container-md',
                    'container-lg',
                    'container-xl',
                    'container-xxl',
                    'container-fluid'
                ],
                default: 'container-fluid',
            },
            bodyBgColor: {
                id: 'bodyBgColor',
                type: 'color',
                label: 'Body Background Color',
                helpText: 'The background color (behind the content) of your layout\'s body area.',
                default: '#ffffffff'
            }
        }
    },
    positions: {
        label: 'Positions',
        id: 'positions',
        type: 'ARRAY',
        helpText: 'Layout Components uses positions to determine where your content should be placed.',
        default: [],
        itemConfig: {
            type: 'ARRAY',
            itemType: 'object',
            primaryKey: 'label',
            permissions: {
                canMove: false,
                canDelete: false,
                allowAddItem: false
            },
            newItem: {
                label: 'Panel',
                components: [],
                panelSettings: {
                    col: 0,
                    row: 0,
                    sizeX: 1,
                    sizeY: 1
                }
            },
            keys: {
                // not configurable
                // label: {}
                components: {
                    label: 'Components',
                    type: 'ARRAY',
                    primaryLabel: 'Component',
                    default: [],
                    helpText: 'List of components that will go in this position in the layout template.',
                    itemConfig: {
                        primaryKey: 'cId',
                        itemType: 'object',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            cId: {
                                type: 'dropdown',
                                id: 'cId',
                                itemsSource: 'componentStore.getList(true)',
                                fields: { text: 'cId', value: 'cId' },
                                label: 'Component ID',
                                helpText: 'ID of component that should go into this position.',
                                default: 'buildInfo',
                                // eslint-disable-next-line max-len
                                itemTemplate: `<div><div style="font-size: 18px; line-height: 1em; padding-top: 12px; padding-bottom: 10px;">{{data.cTitle}}</div><div style="font-size: 15px; border-bottom: 1px solid #eee; padding-bottom: 12px; line-height: 1em;"><span style="margin-right: 20px; font-weight: bold;">{{data.cType}}</span>{{data.cId}}</div></div>`,
                                allowFiltering: true,
                                filterBarPlaceholder: 'any of title: hospital type: chart id: component_id123',
                                onfiltering(e, a, b, cmp) {
                                    let findMatch = (rgx) => {
                                        let query = e.text.toLowerCase();
                                        let match = rgx.exec(query);
                                        if (match && match.length) {
                                            match = match[0].replace(/title:|type:|id:/g, "");
                                            match = match.trim();
                                        }
                                        return match;
                                    };
                                    let keywords = findMatch(/^(.*?)(?=title:|type:|id:|$)/);
                                    let qType = findMatch(/type:(.*?)(?=title:|id:|$)/);
                                    let qTitle = findMatch(/title:(.*?)(?=type:|id:|$)/);
                                    let qId = findMatch(/id:(.*?)(?=title:|type:|$)/);
                                    let output = cmp.options.filter((opt) => {
                                        if (opt.isMissing) return false;
                                        let cId = opt.cId.toLowerCase();
                                        let cType = opt.cType.toLowerCase();
                                        let cTitle = opt.cTitle.toLowerCase();
                                        return (!qId || cId.indexOf(qId) !== -1)
                                            && (!qType || cType.indexOf(qType) !== -1)
                                            && (!qTitle || cTitle.indexOf(qTitle) !== -1)
                                            && (!keywords || (cId.indexOf(keywords) !== -1
                                                || cTitle.indexOf(keywords) !== -1
                                                || cType.indexOf(keywords) !== -1));
                                    });
                                    e.updateData(output);
                                },
                                message: 'Enter keywords to perform a search across all non-prefixed phrases.  Use prefixes to match specific properties such as "type:", "title:", and "id:". Example - "type: chart title: some chart", or "some keywords type: chart.',
                                secondaryActions: [{
                                    text: 'New Component',
                                    action: 'createComponent',
                                    source: 'actionStore',
                                    params: {},
                                    chain: [{
                                        action: 'setSelectedComponent',
                                        source: 'component',
                                        params: {}
                                    }]
                                }]
                            }
                        },
                        newItem: {
                            cId: 'buildInfo'
                        }
                    }
                },
                panelSettings: {
                    type: 'OBJECT',
                    label: 'Panel Settings',
                    id: 'panelSettings',
                    default: {
                        col: 0,
                        row: 0,
                        sizeX: 1,
                        sizeY: 1
                    },
                    properties: {
                        col: {
                            type: 'number',
                            id: 'col',
                            label: 'Column',
                            helpText: 'Column where this panel should show.'
                        },
                        row: {
                            type: 'number',
                            id: 'row',
                            label: 'Row',
                            helpText: 'Row where this panel should show.'
                        },
                        sizeX: {
                            type: 'number',
                            id: 'col',
                            label: 'Panel Width',
                            helpText: 'Height of the panel.'
                        },
                        sizeY: {
                            type: 'number',
                            id: 'col',
                            label: 'Panel Height',
                            helpText: 'Width of the panel.'
                        }
                    }
                }
            }
        },
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'itemConfig.keys.panelSettings.display',
                criteria: 'this.valuesMap.templateFile.indexOf("dynamicTemplates/") > -1'
            },
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'itemConfig.permissions.canDelete',
                criteria: 'this.valuesMap.templateFile.indexOf("dynamicTemplates/") > -1'
            },
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'itemConfig.permissions.allowAddItem',
                criteria: 'this.valuesMap.templateFile.indexOf("dynamicTemplates/") > -1'
            },
            {
                record: 'self',
                valueType: 'value',
                value: 'Panel',
                criteriaRecord: 'component',
                prop: 'itemConfig.primaryLabel',
                criteria: 'this.valuesMap.templateFile.indexOf("dynamicTemplates/") > -1'
            },
            {
                record: 'self',
                valueType: 'value',
                value: false,
                criteriaRecord: 'component',
                prop: 'itemConfig.primaryKey',
                criteria: 'this.valuesMap.templateFile.indexOf("dynamicTemplates/") > -1'
            }
        ],
    },
    displayIf: {
        label: 'Component Enable Logic',
        id: 'displayIf',
        type: 'logicEditor',
        default: [],
        otherProps: {
            recordTypes: [
                { text: 'Layout', value: 'self' },
            ],
            defaultRecordType: 'self'
        },
        helpText: 'Logic for when to enable the components in your layout.',
    },
    showIf: {
        label: 'Show Component Logic',
        id: 'showIf',
        type: 'logicEditor',
        default: [],
        helpText: 'Logic for when to show the components in your layout.',
    },
    layoutDatamap: {
        label: 'Datamap',
        id: 'layoutDatamap',
        type: 'datamapeditor',
        default: [],
        helpText: 'Datamap for connecting components in the layout.',
    },
    layoutDataLogic: {
        label: 'Data Logic',
        id: 'layoutDataLogic',
        type: 'logicEditor',
        otherProps: {
            recordTypes: [
                { text: 'Layout', value: 'self' },
            ],
            defaultRecordType: 'self'
        },
        default: [],
        helpText: 'Use Logic to update data props in the layout.',
    },
    storeProps: {
        label: 'Store Properties',
        id: 'storeProps',
        type: 'ARRAY',
        default: [],
        helpText: 'Store properties are layout specific properties that get added to the local store for connecting data.',
        itemConfig: {
            itemType: 'object',
            primaryKey: 'property',
            permissions: {
                canMove: false,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                property: {
                    type: 'string',
                    id: 'property',
                    label: 'Store Property',
                    helpText: 'The property you want to create on the layout\'s store.'
                },
                type: {
                    type: 'dropdown',
                    id: 'type',
                    label: 'Property Type',
                    default: 'boolean',
                    items: [
                        'boolean',
                        'string',
                        'number',
                        'date'
                    ],
                    helpText: 'The type of data for this property.'
                },
                default: {
                    type: 'boolean',
                    id: 'default',
                    label: 'Default Value',
                    helpText: 'Default value for this property.',
                    logic: [
                        {
                            criteriaRecord: 'component',
                            record: 'self',
                            prop: 'type',
                            valueType: 'path',
                            valuePath: 'controls.storeProps.value[__index__].type',
                            criteria: 'true'
                        }
                    ]
                }
            },
            newItem: {
                property: 'newProperty',
                default: false
            }
        },
    },
    recordTags: {
        label: 'Record Tagging',
        type: 'ARRAY',
        default: [],
        helpText: 'Store properties are layout specific properties that get added to the local store for connecting data.',
        itemConfig: {
            itemType: 'object',
            primaryKey: 'tagName',
            permissions: {
                canMove: false,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                tagName: {
                    label: 'Tag Name',
                    id: 'tagName',
                    type: 'string',
                    default: false,
                    helpText: 'The tag to apply to your record',
                },
                tagSource: {
                    label: 'Tag Record Source',
                    id: 'tagSource',
                    type: 'datasource',
                    itemsSource: 'configStore.getDatasourceList()',
                    fields: {
                        text: 'text',
                        value: 'value',
                        parentValue: 'pId',
                        hasChildren: 'hasChild'
                    },
                    default: []
                },
                tagLogic: {
                    label: 'Tag Logic',
                    id: 'tagLogic',
                    type: 'logicEditor',
                    otherProps: {
                        recordTypes: [
                            { text: 'Layout', value: 'self' },
                            { text: 'Record', value: 'object1' },
                        ],
                        defaultRecordType: 'self'
                    },
                    default: [],
                    helpText: 'Logic to use to determine if the tag applies or not',
                },
            },
            newItem: {
                tagName: 'Tag1',
                tagSource: [],
                tagLogic: []
            },
        }
    },
    preventScrollbars: {
        type: 'OBJECT',
        label: 'Resize Pane Scrollbars',
        helpText: 'Turn off scrollbars for splitter panes',
        default: {
            topRow: false,
            bottomRow: false,
            leftColumn: false,
            rightColumn: false,
        },
        properties: {
            topRow: {
                type: 'boolean',
                label: 'Top Row Scrollbars',
                helpText: 'Disable the top row scrollbars.',
                default: false,
                id: 'topRow',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_row_with_resize.js"'
                    }
                ]
            },
            bottomRow: {
                type: 'boolean',
                label: 'Bottom Row Scrollbars',
                helpText: 'Disable the bottom row scrollbars.',
                default: false,
                id: 'bottomRow',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_row_with_resize.js"'
                    }
                ]
            },
            leftColumn: {
                type: 'boolean',
                label: 'Left Column Scrollbars',
                helpText: 'Disable the left column scrollbars.',
                default: false,
                id: 'leftColumn',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_column_with_resize.js"'
                    }
                ]
            },
            rightColumn: {
                type: 'boolean',
                label: 'Right Column Scrollbars',
                helpText: 'Disable the right column scrollbars.',
                default: false,
                id: 'rightColumn',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_column_with_resize.js"'
                    }
                ]
            },
        },
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.valuesMap.templateFile == "staticTemplates/2_row_with_resize.js" || this.valuesMap.templateFile == "staticTemplates/2_column_with_resize.js"'
            }
        ]
    },
    resizePanelClasses: {
        type: 'OBJECT',
        label: 'Resize Pane Classes',
        helpText: 'Custom CSS classes for resize panel',
        default: {},
        properties: {
            topRow: {
                type: 'string',
                label: 'Top Row CSS Classes',
                helpText: 'Add CSS classes with spaces to the top row.',
                default: '',
                id: 'topRow',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_row_with_resize.js"'
                    }
                ]
            },
            bottomRow: {
                type: 'string',
                label: 'Bottom Row CSS Classes',
                helpText: 'Add CSS classes with spaces to the bottom row.',
                default: '',
                id: 'bottomRow',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_row_with_resize.js"'
                    }
                ]
            },
            leftColumn: {
                type: 'string',
                label: 'Left Column CSS Classes',
                helpText: 'Add CSS classes with spaces to the left column.',
                default: '',
                id: 'leftColumn',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_column_with_resize.js"'
                    }
                ]
            },
            rightColumn: {
                type: 'string',
                label: 'Right Column CSS Classes',
                helpText: 'Add CSS classes with spaces to the right column.',
                default: '',
                id: 'rightColumn',
                logic: [
                    {
                        record: 'self',
                        valueType: 'criteria',
                        criteriaRecord: 'component',
                        prop: 'display',
                        criteria: 'this.valuesMap.templateFile == "staticTemplates/2_column_with_resize.js"'
                    }
                ]
            },
        },
        logic: [
            {
                record: 'self',
                valueType: 'criteria',
                criteriaRecord: 'component',
                prop: 'display',
                criteria: 'this.valuesMap.templateFile == "staticTemplates/2_row_with_resize.js" || this.valuesMap.templateFile == "staticTemplates/2_column_with_resize.js"'
            }
        ]
    },
    expandCollapseButtons: {
        type: 'OBJECT',
        label: 'Expand Collapse Settings',
        helpText: 'Enable buttons to always display for expanding/collapsing resize panels.',
        default: {},
        properties: {
            showLeft: {
                type: 'boolean',
                label: 'Show Left Expand/Collapse Button',
                helpText: 'Displays a button to toggle the expand/collapse state of the left resize panel',
                default: false,
                id: 'showLeft',
            },
            leftExpandIcon: {
                type: 'dropdown',
                label: 'Left Expand Button Icon',
                items: iconItems,
                itemTemplate: `<span><span :class="'e-icons ' + data.value"></span> {{data.text}}</span>`,
                helpText: 'Select an Icon to show for the left button when expanding',
                default: false,
                id: 'leftExpandIcon',
            },
            leftCollapseIcon: {
                type: 'dropdown',
                label: 'Left Collapse Button Icon',
                items: iconItems,
                itemTemplate: `<span><span :class="'e-icons ' + data.value"></span> {{data.text}}</span>`,
                helpText: 'Select an Icon to show for the left button when collapsing',
                default: false,
                id: 'leftCollapseIcon',
            },
            leftButtonText: {
                type: 'string',
                label: 'Left Button Text',
                helpText: 'Enter text to show for the left expand/collapse button',
                default: false,
                id: 'leftButtonText',
            },
            showRight: {
                type: 'boolean',
                label: 'Show Right Expand/Collapse Button',
                helpText: 'Displays a button to toggle the exand/collapse state of the right resize panel',
                default: false,
                id: 'showRight',
            },
            rightExpandIcon: {
                type: 'dropdown',
                label: 'Right Expand Button Icon',
                items: iconItems,
                itemTemplate: `<span><span :class="'e-icons ' + data.value"></span> {{data.text}}</span>`,
                helpText: 'Select an Icon to show for the right button when expanding',
                default: false,
                id: 'rightExpandIcon',
            },
            rightCollapseIcon: {
                type: 'dropdown',
                label: 'Right Collapse Button Icon',
                items: iconItems,
                itemTemplate: `<span><span :class="'e-icons ' + data.value"></span> {{data.text}}</span>`,
                helpText: 'Select an Icon to show for the left button when collapsing',
                default: false,
                id: 'rightCollapseIcon',
            },
            rightButtonText: {
                type: 'string',
                label: 'Right Button Text',
                helpText: 'Enter text to show for the right expand/collapse button',
                default: false,
                id: 'rightButtonText',
            },
        }
    },
    defaultCollapsedPanel: {
        label: 'Default Collapsed Panel',
        type: 'dropdown',
        fields: {
            text: 'text',
            value: 'value',
        },
        items: [
            { text: 'None', value: '' },
            { text: 'Panel 1', value: 'panel1' },
            { text: 'Panel 2', value: 'panel2' }
        ],
        helpText: 'Specify which panel, or none, to be collapsed by default.'
    },
    removeHiddenComponents: {
        type: 'OBJECT',
        label: 'Remove Components When Collapsed',
        message: 'Enable this to improve performance.',
        helpText: 'Removing components from the layout will prevent them from functioning until they are redisplayed.  This can help improve performance in cases where there are a lot of hidden components.',
        default: {},
        id: 'removeHiddenComponents',
        properties: {
            pane0: {
                type: 'boolean',
                label: 'Remove from Left/Top',
                helpText: 'Remove components from left or top panes (pane 1)',
                default: false,
                id: 'pane0',
            },
            pane1: {
                type: 'boolean',
                label: 'Remove from Right/Bottom',
                helpText: 'Remove components from left or top panes (pane 2)',
                default: false,
                id: 'pane1',
            },
        }
    },
    DocumentationUrl: { type: "URL", text: "Layout Container Documentation", url: "https://samaritan.atlassian.net/wiki/spaces/DEVELOPMEN/pages/295207043/layoutContainer" }
};

// TODO remove this hard-coded config
const layoutContainerConfig = {
    "cId": "layoutContainer",
    "cType": "layoutContainer",
    "cTitle": "Static Layout Container",
    "layoutId": "devDash1",
    "msg": "A Test From Local."
};

const locals = {
    getStoreName(iThis, storeProp) {
        return storeProp === 'parentStore'
            ? iThis.parentStore || iThis.storeName
            : iThis.storeName;
    },
    storeNameGenerator(name) {
        let d = new Date();
        return name + "_" + d.getTime();
    },
    hasPositions(cThis) {
        return cThis.positions && !library.objIsEmpty(cThis.positions);
    },
    async getcType(cThis) {
        // TODO remove this after all tempates have been migrated DEVD-464
        // This is used to set the cType so we do not need to keep cType as part of the layoutContainer config
        if (!locals.hasPositions(cThis)) return;
        let promises = [];
        for (let idx in cThis.positions) {
            if (idx === 'length') continue;
            for (let cIdx in cThis.positions[idx].components) {
                if (cIdx === 'length') continue;
                let cId = cThis.positions[idx].components[cIdx].cId;
                promises.push(componentStore.getConfig(cId));
            }
        }
        await library.promiseAllSettled(promises).then((responses) => {
            for (let idx in cThis.positions) {
                if (idx === 'length') continue;
                for (let cIdx in cThis.positions[idx].components) {
                    if (cIdx === 'length') continue;
                    let cId = cThis.positions[idx].components[cIdx].cId;
                    let compRes = responses.find((res) => res.value && res.value.cId === cId);
                    if (compRes) cThis.positions[idx].components[cIdx].cType = compRes.value.cType;
                }
            }
        });
    },
    adjustNames(cThis) {
        if (!locals.hasPositions(cThis)) return;
        let existingComponents = [];
        for (let idx in cThis.positions) {
            if (idx === 'length') continue;
            for (let cIdx in cThis.positions[idx].components) {
                if (cIdx === 'length') continue;
                let cId = cThis.positions[idx].components[cIdx].cId;
                // Push the original cId
                existingComponents.push(cId);
                // Get a count of original cIds
                let count = existingComponents.filter((existingcId) => existingcId === cId).length;
                // If the count is more than 1 append the name to allow the component to render
                if (count > 1) {
                    cId = cId + '__' + count;
                    cThis.positions[idx].components[cIdx].cId = cId;
                }
            }
        }
    },
    setUpDisplay(cThis) {
        if (!locals.hasPositions(cThis)) return;
        let displayedComponents = [];
        let showedComponents = [];
        let setDisplay = (position) => {
            position.forEach((posComponent) => {
                displayedComponents[posComponent.cId] = true;
                showedComponents[posComponent.cId] = true;
                // components.push(posComponent.cId);
            });
        };
        cThis.positions.forEach((position) => {
            if (Array.isArray(position)) {
                setDisplay(position);
            } else if (position.components && Array.isArray(position.components)) {
                setDisplay(position.components);
            }
        });
        cThis.displayedComponents = displayedComponents;
        cThis.showedComponents = showedComponents;
    },
    mapPropData(cThis) {
        if (cThis.config && cThis.config.layoutDatamap) {
            cThis.config.layoutDatamap.forEach((map) => {
                // Set the mapData
                let mapValue;
                if (typeof map.value !== 'undefined' && map.value === 'thisStoreId') mapValue = cThis.storeName;
                else if (typeof map.value !== 'undefined') mapValue = map.value;
                else if (map.layoutProp) mapValue = "layoutProp::" + map.layoutProp;
                else if (map.storeProp) {
                    let storeName;
                    if (map.store === 'parentStore') storeName = cThis.parentStore || cThis.storeName;
                    else if (map.store === 'thisStore') storeName = cThis.storeName;
                    mapValue = "storeProp::" + storeName + "::" + map.storeProp;
                }
                // Apply the value to the mappedPropData
                if (!cThis.mappedPropData[map.to]) cThis.mappedPropData[map.to] = {};
                cThis.mappedPropData[map.to][map.as] = map.property === 'layoutStore'
                    ? cThis.storeName
                    : mapValue;
                // Also be sure to set the parentStore in all mapped properties.
                cThis.mappedPropData[map.to].parentStore = cThis.storeName;
            });
        }
    }
};

const dynamicTemplate = (props, context) => {
    if (typeof dynamicTemplate.uid === "undefined") dynamicTemplate.uid = 0;
    ++dynamicTemplate.uid;
    const template = props && props.template ? props.template : '';
    let dynComponent = {
        template: '<div>Loading...</div>'
    };
    if (!template) return h(dynComponent, context.attrs, context.slots);
    dynComponent = {
        template,
        data() {
            return {
                layoutStoreName: props.storeName,
                layoutDatamap: props.layoutDatamap,
                templateUniqueId: dynamicTemplate.uid,
                editorEnabled: props.editorEnabled,
                mappedPropData: props.mappedPropData,
                layoutTemplateLoaded: props.layoutTemplateLoaded,
                uniqueStoreId: props.uniqueStoreId,
                displayedComponents: {},
                showedComponents: {},
                ...props.data,
                pane0Collapsed: false,
                pane1Collapsed: false,
                showAdjustablePanelContent: false
            };
        },
        async created() {
            await locals.getcType(this);
            locals.adjustNames(this);
            locals.setUpDisplay(this);
            library.logicParser.call(this);
            this.$forceUpdate();
        },
        components: {
            settingsEditor: defineAsyncComponent(() => import('@common/settingsEditor/settingsEditor')),
            "ejs-dashboardlayout": defineAsyncComponent(() => import('@common/syncfusion/dashboardLayout')),
            "e-panel": PanelDirective,
            "e-panels": PanelsDirective,
            "ejs-accordion": defineAsyncComponent(() => import('@common/syncfusion/accordion')),
            "e-accordionitems": AccordionItemsDirective,
            "e-accordionitem": AccordionItemDirective,
            "ejs-sidebar": defineAsyncComponent(() => import('@common/syncfusion/sidebar')),
            "ejs-splitbutton": defineAsyncComponent(() => import('@common/syncfusion/splitButton')),
            "ejs-splitter": SplitterComponent,
            "e-panes": PanesDirective,
            "e-pane": PaneDirective
        },
        methods: {
            async changePanelPositions(event) {
                if (Array.isArray(event.changedPanels)) {
                    let componentSettings = await componentStore.getConfig(this.cId, this.cType);
                    event.changedPanels.forEach((panel) => {
                        let panelSettings = componentSettings.positions[panel.positionIndex]
                            && componentSettings.positions[panel.positionIndex].panelSettings;
                        if (panelSettings) {
                            panelSettings.col = panel.col;
                            panelSettings.row = panel.row;
                        }
                    });
                    library.showToast('You have rearranged your panels. Be sure to save your changes or they will be lost!', { type: 'warning' });
                    componentStore.mutateComponent(this.cId, componentSettings);
                }
            },
            expandCollapse(event) {
                this.pane0Collapsed = event.pane[0].classList.contains('e-pane-hidden');
                this.pane1Collapsed = event.pane[1].classList.contains('e-pane-hidden');
            },
            async storePropsUpdated() {
                locals.setUpDisplay(this);
                library.logicParser.call(this);
            },
            setContentHeight() {
                if (this.$refs && this.$refs.layoutContent) {
                    let height = this.$el.clientHeight - 0.5; // NOSONAR
                    if (this.$refs.headerContent) {
                        height -= this.$refs.headerContent.clientHeight;
                    }
                    if (this.$refs.footerContent) {
                        height -= this.$refs.footerContent.clientHeight;
                    }
                    if (height < 290) height = 290; // NOSONAR
                    this.$refs.layoutContent.style.height = height + 'px';
                }
            },
            toggleSplitter(pane) {
                if (this.$refs.splitter) {
                    let isCollapsed = this['pane' + pane + 'Collapsed'];
                    this['pane' + pane + 'Collapsed'] = !isCollapsed;
                    if (isCollapsed) this.$refs.splitter.expand(pane);
                    else this.$refs.splitter.collapse(pane);
                }
            },
            async updateStoresByDatamap() {
                let getMappedValue = (datamapItem) => {
                    let output;
                    if (typeof this[datamapItem.property] !== 'undefined') {
                        output = this[datamapItem.property];
                    }
                    else {
                        let storeCompKeys = Object.keys(this.$store.state[this.layoutStoreName]);
                        let compKey = storeCompKeys.find((key) => key.split('__')[0] === datamapItem.from);
                        if (compKey) {
                            output = this.$store.state[this.layoutStoreName][compKey][datamapItem.property];
                        }
                    }
                    return output;
                };
                let processLayoutChildren = (index) => {
                    for (let j in this.$refs) {
                        if (j === 'length') continue;
                        if (j.indexOf('ref_') === -1 || !this.$refs[j][0]) continue;
                        if (this.$refs[j][0].storeName && this.$refs[j][0].parentStore && store.state[this.$refs[j][0].parentStore]) {
                            let parentStore = this.$refs[j][0].parentStore;
                            if (typeof store.state[parentStore].childrenLayoutStoreIds === 'undefined') store.state[parentStore].childrenLayoutStoreIds = [];
                            if (store.state[parentStore].childrenLayoutStoreIds.indexOf(this.$refs[j][0].storeName) === -1) {
                                store.state[parentStore].childrenLayoutStoreIds.push(this.$refs[j][0].storeName);
                            }
                        }
                        let newValue = library.cloneObj(this[this.layoutDatamap[index].property]);
                        let storeValue = store.state[this.$refs[j][0].storeName] && store.state[this.$refs[j][0].storeName][this.layoutDatamap[index].as];
                        if (this.$refs[j][0].cId === this.layoutDatamap[index].to
                            && this.$refs[j][0].storeName
                            && store.state[this.$refs[j][0].storeName]
                            && typeof store.state[this.$refs[j][0].storeName][this.layoutDatamap[index].as] !== 'undefined'
                            && !library.isEqualObj(storeValue, newValue)
                        ) {
                            // store.state[this.$refs[j][0].storeName][this.layoutDatamap[index].as] = false;
                            store.state[this.$refs[j][0].storeName][this.layoutDatamap[index].as] = newValue;
                        }
                    }
                };
                for (let i in this.layoutDatamap) {
                    if (i === 'length') continue;
                    let datamapItem = this.layoutDatamap[i];
                    let mappedValue = getMappedValue(datamapItem);
                    if (typeof mappedValue === 'undefined') continue;
                    // if (typeof this[this.layoutDatamap[i].property] === 'undefined') continue;
                    // if (this.layoutDatamap[i].from !== 'thisStore') continue;
                    // eslint-disable-next-line no-await-in-loop
                    let tmpConfig = await componentStore.getConfig(this.layoutDatamap[i].to);
                    if (tmpConfig && tmpConfig.cType === 'layoutContainer') {
                        processLayoutChildren(i);
                    } else {
                        let storeCompKeys = Object.keys(this.$store.state[this.layoutStoreName]);
                        // eslint-disable-next-line no-loop-func
                        let compKey = storeCompKeys.find((key) => key.split('__')[0] === datamapItem.to);
                        if (compKey && store.state[this.layoutStoreName][compKey][datamapItem.as] !== mappedValue) {
                            // store.state[this.layoutStoreName][compKey][datamapItem.as] = false;
                            store.state[this.layoutStoreName][compKey][datamapItem.as] = mappedValue;
                        }
                    }
                }
            },
            dynamicLayoutCreated() {
                this.$nextTick(() => {
                    this.showAdjustablePanelContent = true;
                });
            },
            initMasonry() {
                const container = this.$refs?.masonryContainer;
                if (container) {
                    this.masonryInstance = new Masonry(container, {
                        itemSelector: '.masonry-item',
                        percentPosition: true
                    });
                }
            }
        },
        async updated() {
            this.setContentHeight();
            setTimeout(() => {
                this.$emit('update:childComponentRefs', this.$refs);
            }, 500);
        },
        mounted() {
            let checkCollapsedPanel = true;
            if (this.cId === 'layoutContainer_devDashChild' && typeof library !== 'undefined') {
                let getParams = library.urlParser.getParams();
                if (getParams['instance'] && getParams['repeater']) {
                    checkCollapsedPanel = false;
                }
                this.$nextTick(() => {
                    this.initMasonry();
                });
            }
            if (checkCollapsedPanel && this.defaultCollapsedPanel) {
                this.pane0Collapsed = this.defaultCollapsedPanel === 'panel1';
                if (this.pane0Collapsed) this.$refs.splitter.collapse(0);
                this.pane1Collapsed = this.defaultCollapsedPanel === 'panel2';
                if (this.pane1Collapsed) this.$refs.splitter.collapse(1);
            }
            this.setContentHeight();
            window.addEventListener('resize', this.setContentHeight);
        },
        computed: {
            ...mapState(uStore, {
                user: 'activeUser',
                userStore: 'getAllUsers',
                ecUserProperty: 'getCoordinatorUser',
            }),
            dynamicPositions() {
                let output = [];
                this.positions.forEach((position, positionIndex) => {
                    let panelContentId = "panel_content_"
                        + this.cId.replace(" ", /_/g).toLowerCase()
                        + "_" + positionIndex;
                    let formattedPosition = library.cloneObj(position.panelSettings);
                    formattedPosition.content = `<div id="${panelContentId}"><span class="dynamic-position-identifier">${positionIndex}</span></div>`;
                    formattedPosition.contentId = "#" + panelContentId;
                    formattedPosition.positionIndex = positionIndex;
                    output.push(formattedPosition);
                });
                return output;
            },
            volId() {
                return this.$store.state[this.layoutStoreName].volId;
            },
            oppId() {
                return this.$store.state[this.layoutStoreName].oppId;
            },
            orgId() {
                return this.$store.state[this.layoutStoreName].orgId;
            },
            lbeId() {
                return this.$store.state[this.layoutStoreName].lbeId;
            },
            schId() {
                return this.$store.state[this.layoutStoreName].schId;
            },
            aeId() {
                return this.$store.state[this.layoutStoreName].aeId;
            },
            entryPointId() {
                return this.$store.state[this.layoutStoreName].entryPointId;
            },
            affId() {
                return this.$store.state[this.layoutStoreName].affId;
            },
            dialogId() {
                return this.$store.state[this.layoutStoreName].dialogId;
            },
            positionsFiltered() {
                if (!locals.hasPositions(this)) return [[], [], [], [], [], [], [], []]; // Return empty positions so we don't need v-if in templates
                let positions = library.cloneObj(this.positions);
                for (let i in positions) {
                    if (i === 'length') continue;
                    if (typeof positions[i].components !== 'undefined') {
                        positions[i].components = positions[i].components.filter((tmpCmp) => this.displayedComponents[tmpCmp.cId]);
                    }
                }
                return positions;
            },
            componentMappedProps() {
                let output = {};
                // Setup coreProps for all components in the layout
                if (this.components) {
                    for (let ci in this.components) {
                        if (ci === 'length') continue;
                        let comp = this.components[ci];
                        output[comp.cId] = {
                            contextVolId: this.volId || 0,
                            contextOppId: this.oppId || 0,
                            contextOrgId: this.orgId || 0,
                            contextLbeId: this.lbeId || 0,
                            contextSchId: this.schId || 0,
                            contextAeId: this.aeId || 0,
                            contextEntryPointId: this.entryPointId || 0,
                            contextAffId: this.affId || 0,
                            contextDialogId: this.dialogId || 0
                        };
                    }
                }
                let components = Object.keys(this.mappedPropData);
                components.forEach((comp) => {
                    if (!output[comp]) output[comp] = {};
                    let mapVals = this.mappedPropData[comp];
                    let properties = Object.keys(mapVals);
                    properties.forEach((prop) => {
                        if (typeof mapVals[prop] === 'string' && mapVals[prop].indexOf('layoutProp::') === 0) {
                            output[comp][prop] = this[prop];
                        }
                        else if (typeof mapVals[prop] === 'string' && mapVals[prop].indexOf('storeProp::') === 0) {
                            let storeName = mapVals[prop].split('::')[1];
                            output[comp][prop] = this.$store.state[storeName][prop];
                        }
                        else output[comp][prop] = mapVals[prop];
                    });
                });
                return output;
            },
            containerClasses() {
                let sObj = this.settings;
                let cClass = sObj.layoutWidthType;
                // TODO Update component configs
                if (sObj.layoutWidthType === 'fluid') cClass = 'container-fluid';
                if (sObj.layoutWidthType === 'centered') cClass = 'container';
                if (sObj.containerClasses) cClass += ' ' + sObj.containerClasses;
                return cClass;
            },
            headerClasses() {
                let sObj = this.settings;
                let hClass = '';
                if (sObj.headerClasses) hClass += sObj.headerClasses;
                return hClass;
            },
            headerStyles() {
                let sObj = this.settings;
                let styles = {};
                if (sObj.headerBgColor) styles.background = sObj.headerBgColor;
                if (sObj.textColor) styles.color = sObj.textColor;
                return styles;
            },
            headerLogoStyles() {
                let sObj = this.settings;
                let hsObj = {};
                if (sObj.headerLogoHeight) hsObj.height = sObj.headerLogoHeight;
                if (sObj.headerLogoPadding) hsObj.padding = sObj.headerLogoPadding;
                if (sObj.headerLogoMargin) hsObj.margin = sObj.headerLogoMargin;
                return hsObj;
            },
            containerStyles() {
                let sObj = this.settings;
                let styles = {};
                if (sObj.bodyBgColor) styles.background = sObj.bodyBgColor;
                return styles;
            },
            columnSpecificComponentStyles() {
                let styles = [
                    {},
                    {}
                ];
                if (this.position1ComponentSettings && this.position1ComponentSettings.componentPadding) {
                    styles[0].padding = this.position1ComponentSettings.componentPadding;
                }
                if (this.position2ComponentSettings && this.position2ComponentSettings.componentPadding) {
                    styles[1].padding = this.position2ComponentSettings.componentPadding;
                }
                return styles;
            },
            // Takes the columnRatio from the settings and determines how many columns for the template, concatenating it into a string to use as a class in the template file.
            columnClass() {
                let sObj = this.settings;
                return sObj.columnRatio ? `columns-${sObj.columnRatio.split(':').length}` : 1;
            },
            // Takes the columnRatio from the settings and concatenates them into a new string to use as a class in the template file
            ratioClass() {
                return `ratio-${this.settings?.columnRatio?.split(':').join('-')}`;
            },
            splitPaneClasses() {
                let scrollOpts = this.preventScrollbars;
                let cssClasses = this.resizePanelClasses;
                let classObj = {
                    topRow: '',
                    bottomRow: '',
                    leftColumn: '',
                    rightColumn: '',
                };
                for (let key in cssClasses) {
                    if (key === 'length') continue;
                    if (cssClasses[key]) classObj[key] += ' ' + cssClasses[key];
                }
                // look through top, bottom, left, right to set corresponding panel to 'no-scroll'
                for (let key in scrollOpts) {
                    if (key === 'length') continue;
                    if (scrollOpts[key]) classObj[key] += ' no-scroll';
                }
                // set padding for expand/collapse buttons
                if (this.expandCollapseButtons) {
                    if (this.expandCollapseButtons.showLeft) classObj.rightColumn += ' toggle-expand-right';
                    if (this.expandCollapseButtons.showRight) classObj.leftColumn += ' toggle-expand-left';
                }
                return classObj;
            },
            expandCollapseButtonClasses() {
                let output = {
                    left: 'hide-button',
                    right: 'hide-button',
                };
                if (!this.expandCollapseButtons) return output;
                if (this.expandCollapseButtons.showLeft) {
                    output.right = this.pane0Collapsed ? 'button-collapsed' : 'button-expanded';
                }
                if (this.expandCollapseButtons.showRight) {
                    output.right = this.pane1Collapsed ? 'button-collapsed' : 'button-expanded';
                }
                return output;
            },
            expandCollapseIcon() {
                let output = {
                    right: this.pane0Collapsed ? 'e-right-arrow' : 'e-left-arrow',
                    left: this.pane1Collapsed ? 'e-left-arrow' : 'e-right-arrow'
                };
                let bCfg = this.expandCollapseButtons;
                if (bCfg) {
                    if (this.pane0Collapsed && bCfg.leftExpandIcon) output.right = bCfg.leftExpandIcon;
                    else if (!this.pane0Collapsed && bCfg.leftCollapseIcon) output.right = bCfg.leftCollapseIcon;
                    if (this.pane1Collapsed && bCfg.rightExpandIcon) output.left = bCfg.rightExpandIcon;
                    else if (!this.pane1Collapsed && bCfg.rightCollapseIcon) output.left = bCfg.rightCollapseIcon;
                }
                return output;
            },
            hideComponents() {
                if (this.removeHiddenComponents) return this.removeHiddenComponents;
                return {
                    pane0: false,
                    pane1: false,
                };
            },
            splitterCollapsible() {
                return !this.expandCollapseButtons || (!this.expandCollapseButtons.showLeft && !this.expandCollapseButtons.showRight);
            }
        }
    };
    for (let i in props.layoutDatamap) {
        if (i === 'length') continue;
        let property = props.layoutDatamap[i].property;
        let isStoreProp = props.data.storeProps.find((elmnt) => elmnt.property === property);
        if (typeof dynComponent.watch === 'undefined') dynComponent.watch = {};
        if (typeof dynComponent.watch[property] !== 'undefined') continue;
        dynComponent.watch[property] = (function (iProperty, iIsStoreProp) {
            return function () {
                if (this) {
                    if (iIsStoreProp) this.storePropsUpdated(iProperty);
                    this.updateStoresByDatamap(iProperty);
                }
            };
        }(property, isStoreProp));
    }
    let setStoreProps = () => {
        for (let i in props.data.storeProps) {
            if (i === 'length') continue;
            if (typeof dynComponent.watch === 'undefined') dynComponent.watch = {};
            let property = props.data.storeProps[i].property;
            if (typeof dynComponent.watch[property] === 'undefined') {
                dynComponent.watch[property] = (function (iProperty) {
                    return function () {
                        if (this) this.storePropsUpdated(iProperty);
                    };
                }(property));
            }
        }
    };
    if (props.data && props.data.storeProps) {
        setStoreProps();
    }
    // Generate Computed Props
    let computedKeys = Object.keys(props.computedProps);
    computedKeys.forEach((prop) => {
        let propObj = props.computedProps[prop];
        dynComponent.computed[prop] = function () {
            return typeof store.state[propObj.store][prop] !== 'undefined'
                ? store.state[propObj.store][prop]
                : false;
        };
    });
    return h(dynComponent, context.attrs, context.slots);
};

dynamicTemplate.props = ['storeName', 'template', 'layoutDatamap', 'editorEnabled', 'data', 'mappedPropData', 'layoutTemplateLoaded', 'computedProps', 'uniqueStoreId'];

componentStore.mutateComponent('layoutContainer', layoutContainerConfig);

const layoutContainerComp = {
    name: "layoutContainer",
    editorSettings,
    props: {
        'cId': {},
        'isDisplayed': {
            default: true
        },
        'testConfig': false,
        'parentStore': {},
        isParent: {
            type: Boolean,
            default: false
        },
        editorEnabled: {
            type: Boolean,
            default: false
        },
        uniqueStoreId: {
            default: false
        },
        testMode: {
            type: Boolean,
            default: false
        },
        contextVolId: {
            type: Number
        },
        contextOppId: {
            type: Number
        },
        contextOrgId: {
            type: Number
        },
        contextLbeId: {
            type: Number
        },
        contextSchId: {
            type: Number
        },
        contextAeId: {
            type: Number
        },
        contextEntryPointId: {
            type: Number
        },
        contextAffId: {
            type: Number
        },
        contextDialogId: {
            type: Number
        },
    },
    data() {
        return {
            layoutTemplate: null,
            templateData: false,
            layoutTemplateLoaded: false,
            configLoaded: false,
            storeName: null,
            mappedPropData: {},
            computedPropData: {},
            config: null,
            layoutDatamap: null,
            childComponentRefs: {},
            masonryContainer: ref(null),
            masonryInstance: null
        };
    },
    methods: {
        setChildRefs(refs) {
            this.$emit('update:childComponentRefs', refs);
            this.childComponentRefs = refs;
        },
        openPrintView() {
            let printLoadingComplete = () => { library.hideLoadingIndicator('body'); };
            let printProps = {
                content: this.childComponentRefs.printRef,
                bodyClass: 'print-view',
                onBeforeGetContent: this.beforePrintFunction,
                onBeforePrint: printLoadingComplete,
                onAfterPrint: this.afterPrintFunction,
                pageStyle: this?.config?.commonPrintStyles
            };
            const { handlePrint } = useVueToPrint(printProps);
            // showLoadingIndicator: /* istanbul ignore next */ function (objId, hideAfterSec, message, addClass)
            library.showLoadingIndicator('body', false, `<div class="print-loading-message"><p><i class="fa-regular fa-print-magnifying-glass"></i>Print view generating... please wait.</p></div>`);
            handlePrint();
        },
    },
    computed: {
        dataTestId: {
            get: function () {
                let dataTestId = null;
                let parent = this.$parent;
                for (let i = 0; i < 10; i++) { // NOSONAR
                    if (!parent) break;
                    if (parent && parent.dataTestId) {
                        dataTestId = parent.dataTestId;
                        break;
                    }
                    parent = parent.$parent;
                }
                let prefix = '';
                if (typeof this.uniqueStoreId === 'string') prefix = this.uniqueStoreId + "_";
                else if (this.fromPreview) prefix = 'preview_';
                let cId = this.cId || 'layoutPreview';
                dataTestId = this.testConfig ? '' : dataTestId;
                return prefix + (dataTestId ? dataTestId + '_' : '') + cId;
            }
        },
        layoutBodyStyles() {
            let sObj = this.config.settings;
            let styles = {};
            if (sObj.bodyBgColor) styles.background = sObj.bodyBgColor;
            return styles;
        },
        recordStore() {
            return recordStore();
        },
        uiTagData() {
            // Get Tagged Data
            let output = false;
            if (this.config && this.config.recordTags) {
                // Create tag source subscriptions
                this.config.recordTags.forEach((tagConfig) => {
                    tagConfig = library.cloneObj(tagConfig);
                    // tagName
                    // tagSource
                    // tagLogic
                    let payload = {
                        ...tagConfig.tagSource[0],
                        ...tagConfig
                    };
                    let subscription = this.recordStore.subscribe(payload, this.cId, false, false, this);
                    let storeId = this.isParent ? this.storeName : this.parentStoreId;
                    output = this.recordStore.getSubscription(subscription);
                });
            }
            return output;
        }
    },
    watch: {
        contextVolId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].volId = newVal;
        },
        contextOppId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].oppId = newVal;
        },
        contextOrgId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].orgId = newVal;
        },
        contextLbeId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].lbeId = newVal;
        },
        contextSchId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].schId = newVal;
        },
        contextAeId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].aeId = newVal;
        },
        contextAffId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].affId = newVal;
        },
        contextEntryPointId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].entryPointId = newVal;
        },
        contextDialogId(newVal) {
            if (this?.$store?.state?.[this.storeName]) this.$store.state[this.storeName].dialogId = newVal;
        }
    },
    async created() {
        if (typeof this.$store === 'undefined') this.$store = store;
        // Get template information
        let templates = await configStore.getTemplateList();
        this.templateData = templates;
        // init this layoutContainer component
        // this.config = await componentStore.getConfig(this.cId);
        if (this.testConfig) {
            await library.timeout(500); // NOSONAR
            this.config = this.testConfig;
            this.$forceUpdate();
        } else {
            this.config = await componentStore.getConfig(this.cId);
        }
        // Setup CSS
        if (this.config.componentCssStylesheet) {
            let compStylesheets = configStore.state.cfg.componentStylesheets;
            compStylesheets[this.cId] = new CSSStyleSheet();
            compStylesheets[this.cId]
                .replace(`[cid="${this.cId}"] { & ${this.config.componentCssStylesheet} }`);
            let stylesheets = Object.values(compStylesheets);
            document.adoptedStyleSheets = [...document.adoptedStyleSheets, ...stylesheets];
        }
        // Setup dynamic store
        this.storeName = locals.storeNameGenerator(this.cId);
        let stateObj = {};
        let layoutStore = {
            namespaced: true,
            // State added below
            getters: {}
        };
        // Add Contexted Records to state
        stateObj.volId = this.contextVolId;
        stateObj.oppId = this.contextOppId;
        stateObj.orgId = this.contextOrgId;
        stateObj.lbeId = this.contextLbeId;
        stateObj.schId = this.contextSchId;
        stateObj.aeId = this.contextAeId;
        stateObj.entryPointId = this.contextEntryPointId;
        stateObj.affId = this.contextAffId;
        stateObj.dialogId = this.contextDialogId;
        // Add dynamic state properties
        if (this.config && this.config.storeProps) {
            this.config.storeProps.forEach((propObj) => {
                if (propObj.default === 'false') propObj.default = false;
                if (propObj.default === 'true') propObj.default = true;
                stateObj[propObj.property] = propObj.default;
            });
        }
        // Add state to store
        layoutStore.state = function () {
            return stateObj;
        };
        // Add common getters
        layoutStore.getters.volId = function (state) {
            return state.volId;
        };
        layoutStore.getters.oppId = function (state) {
            return state.oppId;
        };
        layoutStore.getters.orgId = function (state) {
            return state.orgId;
        };
        layoutStore.getters.lbeId = function (state) {
            return state.lbeId;
        };
        // Add dynamic getters
        if (this.config && this.config.storeProps) {
            this.config.storeProps.forEach((propObj) => {
                layoutStore.getters[propObj.property] = function (state) {
                    return state[propObj.property];
                };
            });
        }
        // Generate computed prop data which is passed to (dynamicComponent) to generate computed properties in child components
        if (this.config && this.config.storeProps) {
            this.config.storeProps.forEach((propObj) => {
                if (propObj.property !== 'editorEnabled') {
                    this.computedPropData[propObj.property] = {
                        store: locals.getStoreName(this, propObj.store)
                    };
                }
            });
        }
        this.layoutDatamap = this.config && this.config.layoutDatamap ? this.config.layoutDatamap : false;
        if (this.config && this.config.layoutDatamap) {
            this.config.layoutDatamap.forEach((propObj) => {
                if (propObj.store) {
                    let propStoreName;
                    if (propObj.store === 'thisStore') propStoreName = this.storeName;
                    else if (propObj.store === 'parentStore') propStoreName = this.parentStore;
                    else propStoreName = propObj.store;
                    this.computedPropData[propObj.property] = {
                        store: propStoreName
                    };
                }
            });
        }
        // Generate mutations
        // TODO: Generate mutations from config file
        // Register Store with dynamic namespace
        store.registerModule(
            this.storeName,
            layoutStore
        );
        this.configLoaded = true;
    },
    unmounted() {
        if (this && this.recordStore) {
            this.recordStore.unsubscribe(false, this.cId);
        }
    },
    updated() {
        if (this.config && !library.objIsEmpty(this.config) && this.templateData && !this.layoutTemplateLoaded) {
            // Maps props directly to layout
            // TODO determine if this is how we want to do it
            // Set Layout Props (relative properties)
            if (this.config && this.config.layoutProps) {
                let props = Object.keys(this.config.layoutProps);
                props.forEach((prop) => {
                    this[prop] = this.config.layoutProps[prop];
                });
            }
            this.layoutDatamap = this.config.layoutDatamap || false;
            // Maps props directly to layout
            // TODO determine if this is how we want to do it
            // Distributes props to sub components in the layout
            locals.mapPropData(this);
            // Connect the templateFile
            let template = this.templateData.find((tmpTemplate) => tmpTemplate.name === this.config.templateFile);
            if (typeof template === 'object') template = template.templateFile;
            else template = this.config.templateFile;
            if (!template) {
                template = 'staticTemplates/template_not_found.js';
            }
            import('./' + template)
                .then((result) => {
                    this.layoutTemplate = result.default;
                    // Set the layout as loaded
                    this.layoutTemplateLoaded = true;
                })
                .catch((result) => { errorHandler.log(result); });
        }
    },
    components: {
        dynamicTemplate,
        settingsCog: defineAsyncComponent(() => import('@common/settingsEditor/settingsEditorCog')),
        "ejs-accordion": defineAsyncComponent(() => import('@common/syncfusion/accordion')),
        "e-accordionitems": AccordionItemsDirective,
        "e-accordionitem": AccordionItemDirective,
        "ejs-sidebar": defineAsyncComponent(() => import('@common/syncfusion/sidebar')),
        "ejs-splitbutton": defineAsyncComponent(() => import('@common/syncfusion/splitButton')),
        "ejs-splitter": SplitterComponent,
        "e-panes": PanesDirective,
        "e-pane": PaneDirective
    }
};
componentStore.state.componentsConfig['layoutContainer'] = layoutContainerComp;
// componentStore.initComponent(layoutContainerComp, layoutContainerConfig);
export { locals, layoutContainerComp, dynamicTemplate };
export default layoutContainerComp;
</script>

<style>
    .layout-container-wrapper {
        height: 100%;
    }
    .layout-container-wrapper > div {
        height: 100%;
    }
    .layout-container {
        height: 100%;
    }
    .template-not-found {
        width: 90%;
        margin: 0 auto;
        padding-top: 100px;
    }

    .template-not-found > div {
        text-align: center;
    }
    .no-scroll {
        overflow: hidden;
        width: 100%;
        height: 100%;
    }
    .layout-component > div + div {
        overflow: hidden;
    }
    /* TODO: Move this to a new component file with dynamicComponent */
    .dynamic-position-identifier {
        display: none;
        text-align: right;
        font-size: 24px;
        position: absolute;
        z-index: 100;
        width: 100%;
        margin-top: 10px;
        padding-right: 10px;
    }
    .e-panel-content {
        overflow: auto;
    }
</style>
<style scoped>
.ui-tags {
    display: none;
}

:deep(.masonry-layout) {
    container-type: inline-size;
    container-name: masonry-layout;
}
:deep(.masonry) {
    display: grid;
    grid-template-rows: auto;
    gap: 1rem;
}

/* Default equal columns */
:deep(.masonry.columns-2) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 1fr);
}

:deep(.masonry.columns-3) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 1fr) minmax(300px, 1fr);
}

:deep(.masonry.columns-4) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 1fr) minmax(300px, 1fr) minmax(300px, 1fr);
}

/* Custom ratios 2 columns */
:deep(.masonry.columns-2.ratio-1-1) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 1fr);
}
:deep(.columns-2.ratio-1-2) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 2fr);
}
:deep(.columns-2.ratio-1-3) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 3fr);
}
:deep(.columns-2.ratio-2-1) {
    grid-template-columns: minmax(300px, 2fr) minmax(300px, 1fr);
}
:deep(.columns-2.ratio-3-1) {
    grid-template-columns: minmax(300px, 3fr) minmax(300px, 1fr);
}

/* Custom ratios 3 columns */
:deep(.masonry.columns-3.ratio-2-1-1) {
    grid-template-columns: minmax(300px, 2fr) minmax(300px, 1fr) minmax(300px, 1fr);
}
:deep(.masonry.columns-3.ratio-1-2-1) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 2fr) minmax(300px, 1fr);
}
:deep(.masonry.columns-3.ratio-1-1-2) {
    grid-template-columns: minmax(300px, 1fr) minmax(300px, 1fr) minmax(300px, 2fr);
}

@container masonry-layout (max-width: 1248px) {
    :deep(.masonry.columns-4) {
        display: block;
        column-count: 3;
    }
}

@container masonry-layout (max-width: 932px) {
    :deep(.masonry.columns-3) {
        display: block;
        column-count: 2;
    }
    :deep(.masonry.columns-4) {
        column-count: 2;
    }
}

@container masonry-layout (max-width: 616px) {
    :deep(.masonry.masonry.columns-4) {
        column-count: 1;
    }
    :deep(.masonry.masonry.columns-3) {
        column-count: 1;
    }
    :deep(.masonry.masonry.columns-2) {
        display: block;
        column-count: 1;
    }
}
:deep(.masonry-item) {
    break-inside: avoid;
    margin-bottom: 1rem;
}

:deep(.expand-collapse-button) {
    background: rgba(0, 0, 0, 0.1);
    padding: 6px;
    margin: 5px 2px 2px 2px;
    position: absolute;
    z-index: 10;
    color: #0e4a86;
    font-size: 16px;
    line-height: 10px;
    border-radius: 50%;
    opacity: 0.8;
    border: 0 none;
    cursor: pointer;
}
:deep(.expand-collapse-button:hover) {
    opacity: 1;
}
:deep(.hide-button) {
    display: none !important;
}
:deep(.collapse-left) {
    right: 0;
}
:deep(.collapse-right) {
    left: 0;
}
/* Icons for expand/collapse button */
:deep(.e-icons.e-close:before) {
    content: '\ec0f' !important;
}
:deep(.e-icons.e-double-left-arrow:before) {
    content: '\e656' !important;
}
:deep(.e-icons.e-double-right-arrow:before) {
    content: '\e655' !important;
}
:deep(.e-icons.e-back-arrow:before) {
    content: '\e977' !important;
}
:deep(.e-icons.e-left-arrow:before) {
    content: '\e85b' !important;
}
:deep(.e-icons.e-right-arrow:before) {
    content: '\e85f' !important;
}
:deep(.e-icons.e-left-chevron:before) {
    content: '\e937' !important;
}
:deep(.e-icons.e-right-chevron:before) {
    content: '\e956' !important;
}
:deep(.e-icons.e-menu-1:before) {
    content: '\e99a' !important;
}
:deep(.e-icons.e-menu-2:before) {
    content: '\e984' !important;
}
:deep(.e-icons.e-search:before) {
    content: '\e275' !important;
}

:deep(.header-content .header-top-row) {
    display: flex;
}

:deep(.header-content .header-top-row .header-text) {
    margin: 0;
    flex-grow: 9;
}
:deep(.toggle-expand-left) {
    padding-right: 30px;
}
:deep(.toggle-expand-right) {
    padding-left: 30px;
}
:deep(.toggle-expand-left .e-splitter.e-splitter-vertical) {
    border-right: 0 none;
}
:deep(.toggle-expand-right .e-splitter.e-splitter-vertical) {
    border-left: 0 none;
}
</style>
